import type { ReactNode } from "react";
import { Navigate } from "react-router";
import {
  MODULE_FEATURE,
  grantOf,
  type PermissionAction,
  type PermissionModule,
  type PermissionScope,
} from "@gatiq/shared";
import { Card, EmptyState, Page, PageHeader, Spinner } from "@/ui";
import { navFor } from "@/layout/nav";
import { useAuth } from "./AuthProvider";
import { homeFor } from "./RequireAuth";
import { useOrgRoles } from "./useOrgRoles";

/**
 * The signed-in person's reach for `action` on `module` — `"own"`, `"org"`,
 * or null when their role does not have it (D-16). Presentation only: the API
 * checks the same grid on every request.
 */
export function useCan(): (module: PermissionModule, action?: PermissionAction) => PermissionScope | null {
  const { user } = useAuth();
  return (module, action = "view") => grantOf(user?.permissions, module, action);
}

/**
 * Route guard for a console page: the page's module must be viewable, or the
 * page is the org admin's alone (`admin`). Anyone else goes home.
 */
export function Gate({
  module,
  admin = false,
  children,
}: {
  module?: PermissionModule;
  admin?: boolean;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const roles = useOrgRoles();
  if (!user) return null;
  const feature = module ? MODULE_FEATURE[module] : undefined;
  if (feature && !roles) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-brand" />
      </div>
    );
  }
  const allowed =
    (admin ? user.role === "org_admin" : Boolean(module && grantOf(user.permissions, module, "view"))) &&
    (!feature || Boolean(roles?.features?.[feature]));
  return allowed ? children : <Navigate replace to={homeFor(user)} />;
}

/**
 * `/:orgSlug/sales` — where a sales rep or distributor lands. Their counters
 * when they may see them (the page they came here for before D-16), otherwise
 * the first page their role was given.
 */
export function StaffHome() {
  const { user } = useAuth();
  const roles = useOrgRoles();
  if (!user) return null;
  if (!roles) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-brand" />
      </div>
    );
  }
  const items = navFor(user, roles);
  const first = items.find((i) => i.to.endsWith("/counters")) ?? items[0];
  if (first) return <Navigate replace to={first.to} />;
  return (
    <Page>
      <PageHeader title="Welcome" />
      <Card fill>
        <EmptyState
          message="Your organization has not opened any pages to your role yet. Ask your admin for access."
          title="Nothing to show yet"
        />
      </Card>
    </Page>
  );
}
